import { findAnchor } from '@common/utils/flow';
import { AnchorType, FullEdge, SourceAnchorType } from '@components/Flow';
import { ModNodeState, ModuleNodeState, RequestNodeState } from '@components/Flow/Nodes';
import { Command, State, Tab } from '@store/index';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useModules } from './useModules';
import { GlobalVariable } from '@components/Workspace/routes/GlobalVariables/types';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

const PASS_THROUGH_NODES = ['Branch', 'Switch'];

export const useNodeData = (command_id: string | null = null) => {
  const edges = useSelector((state: State) => state.tabs.active.edges);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const modules = useSelector((state: State) => state.tabs.active.modules);
  const dataPrefixes = useSelector((state: State) => state.tabs.active?.flow?.data_prefixes);
  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { allModules } = useModules();

  const globalVariablesQK = ['globalVariables', service_id];
  const globalVariables = (queryClient.getQueryData(globalVariablesQK) as GlobalVariable[]) || [];

  const isStreamDisabled = (id: string) => {
    const command = getCommand(id);
    if (command?.config?.command_id !== 'http.response') return true;

    const connections = getInputDataConnections(id);

    if (connections?.length === 1) {
      const connectionCommand = getCommand(connections[0].edge.source);

      if (connectionCommand.config.command_id === 'node.module') {
        const moduleId = connectionCommand.state.moduleId;
        const mod = allModules.find((mod) => mod.flow_id === moduleId);
        return !mod?.streamable;
      }

      const allowedStreamCommands = ['llm.completion', 'assistant.router', 'assistant.single'];
      return !allowedStreamCommands.includes(connectionCommand.config.command_id);
    }

    return true;
  };

  const getPluginDataConnections = (command: Command, allowToolArguments = false) => {
    const plugins = ['Tool'];

    if (plugins.includes(command.config.identifier)) {
      const pluginTarget = command.connections.find((connection) => connection.target_anchor.variant === 'plugin');
      const targetNode = commands.find((cmd) => cmd.node.id === pluginTarget?.edge.target);

      if (targetNode?.connections) {
        const connections = targetNode.connections.filter(
          (connection) =>
            connection.edge.target === targetNode?.node?.id && connection?.target_anchor?.variant === 'data'
        );

        if (command.config.identifier === 'Tool' && allowToolArguments) return connections.concat(command.connections);
        return connections;
      }
    }

    const connections = command.connections.filter(
      (connection) => connection.edge.target === command.node.id && connection?.target_anchor?.variant === 'data'
    );

    return connections;
  };

  const getStreamEdge = (id: string) => {
    const command = getCommand(id);

    const isModuleNode = command?.config?.command_id === 'node.module';
    const isCompletionNode = command?.config?.command_id === 'llm.completion';
    const isResponseNode = command?.config?.command_id === 'http.response';
    const isAssistantNode = command?.config?.command_id === 'assistant.single';
    const isAssistantRouterNode = command?.config?.command_id === 'assistant.router';

    if (isAssistantNode) {
      const streamEdge = edges.find(
        (edge) =>
          edge.source === id &&
          edge.targetHandle === 'http.response.data' &&
          edge.sourceHandle === 'assistant.single.output_data'
      );

      return streamEdge;
    }

    if (isAssistantRouterNode) {
      const streamEdge = edges.find(
        (edge) =>
          edge.source === id &&
          edge.targetHandle === 'http.response.data' &&
          edge.sourceHandle === 'assistant.router.output_data'
      );

      return streamEdge;
    }

    if (isModuleNode) {
      const streamEdge = edges.find(
        (edge) =>
          edge.source === id &&
          edge.targetHandle === 'http.response.data' &&
          edge.sourceHandle === 'node.module.output_data'
      );

      return streamEdge;
    }

    if (isCompletionNode) {
      const streamEdge = edges.find(
        (edge) =>
          edge.source === id &&
          edge.targetHandle === 'http.response.data' &&
          edge.sourceHandle === 'llm_completion.output_data'
      );

      return streamEdge;
    }

    if (isResponseNode) {
      const streamEdge = edges.find(
        (edge) =>
          edge.target === id &&
          edge.targetHandle === 'http.response.data' &&
          edge.sourceHandle === 'llm_completion.output_data'
      );

      return streamEdge;
    }
  };

  const isSourceStreamEnabled = (id: string, label: string, streamCommand: Command) => {
    const command = getCommand(id);

    if (streamCommand?.config?.command_id === 'node.module') {
      const mod = allModules.find((mod) => mod.flow_id === streamCommand?.state?.moduleId);
      if (!mod?.streamable) return false;

      const connections = command.connections.filter(
        (connection) => connection?.target_anchor?.variant === 'data' && connection?.edge?.source === id
      );

      if (label === 'Data' && connections.length === 1) {
        const target_id = connections[0].edge.target;
        const connectionCommand = getCommand(target_id);
        const connectionInputs = getInputDataConnections(target_id);
        return connectionCommand?.state?.response_method === 'stream' && connectionInputs.length === 1;
      }
    }

    const allowedStreamCommands = ['llm.completion', 'assistant.router', 'assistant.single'];
    if (!allowedStreamCommands.includes(streamCommand?.config?.command_id)) return false;

    const connections = command.connections.filter(
      (connection) => connection?.target_anchor?.variant === 'data' && connection?.edge?.source === id
    );

    if (label === 'Data' && connections.length === 1) {
      const target_id = connections[0].edge.target;
      const connectionCommand = getCommand(target_id);
      const connectionInputs = getInputDataConnections(target_id);
      return connectionCommand?.state?.response_method === 'stream' && connectionInputs.length === 1;
    }

    return false;
  };

  const nodeEdges = useMemo(() => {
    return edges.filter((edge) => edge?.source === command_id);
  }, [edges, command_id]);

  const dataEdges = useMemo(() => {
    const edges = nodeEdges.map((edge) => {
      const anchor = findAnchor(edge.sourceHandle as string, commands);
      if (anchor?.variant === 'data') return anchor;
    });

    return edges.filter(Boolean) as AnchorType[];
  }, [nodeEdges]);

  // compute the global variables into a usable object with key value pairs
  const globalOptions = useMemo(
    () =>
      globalVariables.reduce(
        (
          acc: {
            global: { variable: Record<string, string> };
          },
          curr: GlobalVariable
        ) => {
          acc.global.variable[curr.key] = curr.value;
          return acc;
        },
        { global: { variable: {} } }
      ),
    [globalVariables]
  );

  const getOptions = useMemo(
    () =>
      (id: string, allowToolArgs = false) => {
        const command = getCommand(id);

        const connections = getInputDataConnections(id);
        if (connections === undefined || connections === null) return [];

        const dataCommands = getCommands(connections);
        if (dataCommands === undefined || dataCommands === null) return [];

        if (command?.config?.identifier === 'Tool') {
          // add the tool arguments as output data

          const pluginEdge = command.connections.find(
            (conn) => conn.target_anchor.variant === 'plugin' && conn.edge.source === id
          );

          // find the completion node via the plugin edge and get the input data for this node
          if (pluginEdge) {
            if (allowToolArgs) dataCommands.push({ command, anchor: pluginEdge.target_anchor });
            const pluginConnections = getInputDataConnections(pluginEdge?.edge.target);
            const pluginCommands = getCommands(pluginConnections);

            for (const pluginCommand of pluginCommands) {
              dataCommands.push(pluginCommand);
            }
          }
        }

        const output = parseInputData(dataCommands);
        const prefixData = getPrefixData(id);
        const allGlobalData = { ...prefixData, ...globalOptions };
        const hasGlobalData = Object.keys(allGlobalData || {}).length > 0;

        if (output === 'null' && !hasGlobalData) return {};
        if (output === 'null' && hasGlobalData) return allGlobalData;
        if (output !== 'null' && hasGlobalData) {
          return { ...output, ...allGlobalData };
        }
      },
    [edges]
  );

  const getPrefixData = (node_id: string) => {
    const prefixes = dataPrefixes || {};

    let allData: { [key: string]: any } = {};

    for (const [nodeId, prefix] of Object.entries(prefixes)) {
      const command = getCommand(nodeId);
      const dataAnchors =
        command?.config?.anchors.filter((anchor) => anchor.variant === 'data' && anchor.type === 'source') || [];

      if (nodeId !== node_id) {
        const isWebhookNode = command?.config?.webhooks && command?.config?.webhooks?.length > 0;

        if (isWebhookNode) {
          let webhookData: { [key: string]: any } = {};
          for (const anchor of dataAnchors) {
            const outputData = getOutputData(nodeId, anchor);
            if (outputData !== 'null') webhookData = mergeCommandData(webhookData, command, outputData, anchor);
          }

          allData = { ...allData, [prefix]: webhookData };
        } else {
          for (const anchor of dataAnchors) {
            const outputData = getOutputData(nodeId, anchor);

            const prefixOutputData = { [prefix]: outputData };
            if (outputData !== 'null') {
              allData = mergeCommandData(allData, command, prefixOutputData, anchor);
            }
          }
        }
      }
    }

    return allData;
  };

  /**
   * Used to the data for a target data anchor (data going into a node)
   * @param id the node id
   * @param fold If true, the data will be folded for preview usage only
   */

  const getInputData = useMemo(
    () =>
      (id: string, fold = false, overrideCommands: Command[] | null = null) => {
        const command = getCommand(id, overrideCommands);

        const connections = getInputDataConnections(id, overrideCommands);
        if (connections === undefined || connections === null) return 'null';

        const dataCommands = getCommands(connections, overrideCommands);
        if (dataCommands === undefined || dataCommands === null) return 'null';

        const isResponseNode = command?.config?.identifier === 'Response';
        const isCustomSchema = command?.state?.format_response === 'true';

        // if a custom schema was defined, show the custom schema instead
        if (isResponseNode && isCustomSchema) {
          const dynamicOutput = command.output.dynamic;

          if (dynamicOutput) return dynamicOutput;

          try {
            return JSON.parse(command.state.format_schema.value);
          } catch {
            return command.state.format_schema.value;
          }
        }

        /* if this is a tool node, we need to get the data inputs for the completion node 
          it is connected to via a plugin edge */

        const isToolNode = command?.config?.identifier === 'Tool';
        if (isToolNode) {
          const pluginEdge = command.connections.find(
            (conn) => conn.target_anchor.variant === 'plugin' && conn.edge.source === id
          );

          if (pluginEdge) {
            const pluginConnections = getInputDataConnections(pluginEdge?.edge.target, overrideCommands);
            const pluginCommands = getCommands(pluginConnections, overrideCommands);
            for (const pluginCommand of pluginCommands) {
              dataCommands.push(pluginCommand);
            }
          }
        }

        try {
          const output = parseInputData(dataCommands, fold, overrideCommands);
          return output;
        } catch (e) {
          console.error('Failed to load input data', e);
        }
      },
    [commands, modules]
  );

  const parseInputData = (
    dataCommands: { command: Command; anchor: AnchorType }[],
    fold = false,
    overrideCommands: Command[] | null = null
  ) => {
    let allData: { [key: string]: any } = {};

    const isMultipleData = dataCommands.length > 1;
    const sourceAnchors = dataCommands
      .map((item) => ({ ...item.anchor, source: item.command.node.id }))
      .filter((item) => item.type === 'source') as SourceAnchorType[];

    const isDuplicateOutputs = hasDuplicateNodeType(
      dataCommands.map((item) => item.command),
      sourceAnchors
    );

    dataCommands.forEach((item) => {
      const output = getOutputData(item.command.node.id, item.anchor, false, overrideCommands);
      const node_id = isMultipleData && isDuplicateOutputs ? item?.command.node.id : undefined;
      const isTool = item?.command?.config.identifier === 'Tool';
      const hasToolArgs = item?.command?.state?.args?.length > 0 && item?.command?.state?.args?.some((arg) => arg.name);

      // tool data is retrieved from the llm node it's connected to
      if (isTool && hasToolArgs) {
        const toolArguments: { [key: string]: string } = {};

        for (const arg of item?.command.state.args.filter((arg) => arg.name)) {
          toolArguments[arg.name] = '';
        }

        allData = { ...allData, tool: { args: toolArguments } };
      }

      if (!isTool) {
        if (isMultipleData && isDuplicateOutputs && node_id) {
          const nodeIdentifier = `node_${node_id}`;
          allData = mergeCommandData(allData, item.command, { [nodeIdentifier]: output }, item.anchor, nodeIdentifier);
        } else if (output !== 'null') {
          allData = mergeCommandData(allData, item.command, output, item.anchor);
        } else {
          const [field, value] = item.command.config.command_id.split('.');
          allData = { ...allData, [field]: { [value]: output } };
        }
      }
    });

    try {
      if (Object.keys(allData).length === 0) return 'null';
      else if (fold && isMultipleData) {
        const truncated = truncateObject(allData, 3);
        if (typeof truncated === 'object') return truncated;
        return allData;
      } else return allData;
    } catch (e) {
      return allData;
    }
  };

  /**
   * Used to merge multiple node outputs together.
   * Handles outputs with a similar structure and merges objects seemlessly.
   * @param allData The entire data output of all nodes already added
   * @param command The command being added to the data output
   * @param output The output of the command
   * @param anchor The anchor of the command
   * @returns allData merged with output is returned.
   */

  const mergeCommandData = (
    allData: { [key: string]: any },
    command: Command,
    output: any,
    anchor: AnchorType,
    nodeIdentifier?: string
  ) => {
    const commandId = getWebhookId(command, anchor) || command.config.command_id;
    const parts = nodeIdentifier ? [nodeIdentifier, ...commandId.split('.')] : commandId.split('.');

    const result = { ...allData };
    const [firstPart, secondPart, thirdPart] = parts;

    // If this is the first entry for this command type
    if (!result[firstPart]) {
      return { ...result, ...output };
    }

    if (secondPart) {
      result[firstPart] = { ...result[firstPart] };

      if (thirdPart) {
        result[firstPart][secondPart] = {
          ...result[firstPart][secondPart],
        };

        const restParts = parts.slice(3);
        if (restParts.length > 0) {
          // Four-level nesting (e.g, node_1.call.speak.ended)
          result[firstPart][secondPart][thirdPart] = {
            ...(result[firstPart][secondPart][thirdPart] || {}),
            [restParts[0]]: output[firstPart][secondPart][thirdPart][restParts[0]],
          };
        } else {
          // Three-level nesting (e.g., call.speak.ended)
          result[firstPart][secondPart][thirdPart] = {
            ...(result[firstPart][secondPart][thirdPart] || {}),
            ...output[firstPart][secondPart][thirdPart],
          };
        }
      } else {
        // Two-level nesting (e.g., call.initiated)
        result[firstPart][secondPart] = {
          ...(result[firstPart][secondPart] || {}),
          ...output[firstPart][secondPart],
        };
      }
    }

    return result;
  };

  /**
   * Used to the data for a source data anchor (data coming out of a node)
   * @param id the node id
   * @param anchor the anchor of the data edge
   * @param fold true if you want to fold the data for preview only
   */

  const getOutputData = useMemo(
    () =>
      (id: string, anchor: AnchorType, fold = false, overrideCommands: Command[] | null = null) => {
        const command = getCommand(id, overrideCommands);

        if (command.config.command_id === 'http.request' && command)
          return getRequestTriggerOutputData(id, overrideCommands);
        if (command.config.command_id === 'workflow.module' && command)
          return getModuleTriggerOutputData(id, overrideCommands);

        const isPassThroughNode = PASS_THROUGH_NODES.includes(command?.config?.identifier);
        if (isPassThroughNode) return getInputData(id, fold, overrideCommands);

        const webhookId = getWebhookId(command, anchor);
        const defaultOutput = command.output?.default;
        const dynamicOutput = command.output?.dynamic;

        if (webhookId) {
          if (command.config.command_id === 'call.ai_gather' && command)
            return getAiGatherOutputData(id, webhookId, overrideCommands);

          return dynamicOutput?.[webhookId] || defaultOutput?.[webhookId];
        }

        const output = getNodeOutput(command, anchor);
        if (!output) return 'null';

        if (command?.config) return output;
        return 'null';
      },
    [commands, getInputData, modules]
  );

  /**
   * When you add a parameter to the trigger request, this parameter needs
   * to dynamically be shown in the data preview when hovering the data anchor
   * This method will get those values and insert them into the data object
   */

  const getRequestTriggerOutputData = useMemo(
    () =>
      (id: string, overrideCommands: Command[] | null = null) => {
        const command = getCommand(id, overrideCommands);

        const state: RequestNodeState = command.state;
        const queryParams = state.query_parameters;
        const bodyParams = state.body_parameters;

        const query: { [key: string]: string } = {};
        const body: { [key: string]: string } = {};

        queryParams.forEach(
          (param) => (query[param.name] = command?.output?.dynamic?.http?.request?.query?.[param.name] || '')
        );
        bodyParams.forEach(
          (param) => (body[param.name] = command?.output?.dynamic?.http?.request?.body?.[param.name] || '')
        );

        if (command.output.default !== null) {
          return {
            http: {
              request: {
                ...command.output?.default?.http?.request,
                ...command.output?.dynamic?.http?.request,
                query,
                body,
              },
            },
          };
        }

        return { http: { request: { query, body } } };
      },
    [commands, getInputData, modules]
  );

  const getModuleTriggerOutputData = useMemo(
    () =>
      (id: string, overrideCommands: Command[] | null = null) => {
        const command = getCommand(id, overrideCommands);

        const state: ModuleNodeState = command.state;
        const params = state.parameters;
        const data: { [key: string]: string } = {};

        params.forEach(
          (param) => (data[param.name] = command.output?.dynamic?.workflow?.module?.[param.name] || param.type)
        );

        if (Object.keys(data).length > 0) {
          return {
            workflow: {
              module: data,
            },
          };
        }

        return { module: { parameters: {} } };
      },
    [commands, getInputData, modules]
  );

  const getAiGatherOutputData = useMemo(
    () =>
      (id: string, webhookId: string, overrideCommands: Command[] | null = null) => {
        const command = getCommand(id, overrideCommands);

        const state = command.state;

        const defaultOutput = command.output.default;
        const dynamicOutput = command.output.dynamic;

        if (webhookId === 'call.ai_gather.ended') {
          const output = dynamicOutput?.['call.ai_gather.ended'] ? dynamicOutput : defaultOutput;
          const params = state.args;
          const data: { [key: string]: string } = {};

          params.forEach(
            (param) =>
              (data[param.name] =
                command.output?.dynamic?.['call.ai_gather.ended']?.call?.ai_gather?.ended?.result?.[param.name] ||
                param.type)
          );

          if (Object.keys(data).length > 0) {
            return {
              call: {
                ai_gather: {
                  ended: {
                    ...output?.['call.ai_gather.ended']?.call?.ai_gather?.ended,
                    result: data,
                  },
                },
              },
            };
          }

          return command.output.default?.['call.ai_gather.ended'];
        }

        if (webhookId === 'call.ai_gather.partial_results') {
          const output = dynamicOutput?.['call.ai_gather.partial_results'] ? dynamicOutput : defaultOutput;
          const params = state.args;
          const data: { [key: string]: string } = {};

          params.forEach(
            (param) =>
              (data[param.name] =
                command.output?.dynamic?.['call.ai_gather.partial_results']?.call?.ai_gather?.partial_results
                  ?.partial_results?.[param.name] || param.type)
          );

          if (Object.keys(data).length > 0) {
            return {
              call: {
                ai_gather: {
                  partial_results: {
                    ...output?.['call.ai_gather.partial_results']?.call?.ai_gather?.partial_results,
                    partial_results: data,
                  },
                },
              },
            };
          }

          return command.output.default?.['call.ai_gather.partial_results'];
        }

        return 'null';
      },
    [commands, getInputData, modules]
  );

  const getCommand = (id: string, overrideCommands: Command[] | null = null): Command => {
    const inputCommands = overrideCommands && overrideCommands?.length > 0 ? overrideCommands : commands;
    const command = inputCommands.find((command) => command.node.id === id);
    return command as Command;
  };

  const getCommands = (
    connections: FullEdge[],
    overrideCommands: Command[] | null = null
  ): { command: Command; anchor: AnchorType }[] => {
    const uniqueCommands = new Map<string, { command: Command; anchor: AnchorType }>();

    connections.forEach((connection) => {
      const command = getCommand(connection.edge.source, overrideCommands);

      // Create a unique key based on node.id and sourceHandle
      const uniqueKey = `${command.node.id}-${connection.edge.sourceHandle || 'default'}`;

      // Only add if this unique key hasn't been encountered
      if (!uniqueCommands.has(uniqueKey)) {
        uniqueCommands.set(uniqueKey, { command, anchor: connection.source_anchor });
      }
    });

    return Array.from(uniqueCommands.values());
  };

  const getInputDataConnections = (id: string, overrideCommands: Command[] | null = null) => {
    const command = getCommand(id, overrideCommands);
    if (!command) return [];

    const connections = command.connections.filter(
      (conn) => conn?.target_anchor?.variant === 'data' && conn?.edge?.target === id
    );

    const output: FullEdge[] = [];

    for (const connection of connections) {
      const { edge } = connection;
      const { source } = edge;

      const connectionCommand = getCommand(source, overrideCommands);
      const isPassThroughNode = PASS_THROUGH_NODES.includes(connectionCommand?.config?.identifier);

      // if the node is a passthrough node, we need to get the source data
      if (isPassThroughNode) {
        const source_node_id = connection.edge.source;
        output.push(...getInputDataConnections(source_node_id, overrideCommands));
      } else output.push(connection);
    }

    return output;
  };

  const getDataConnections = (command: Command, allowToolArguments = false) => {
    const plugins = ['Tool'];

    if (plugins.includes(command.config.identifier)) {
      const pluginTarget = command.connections.find((connection) => connection.target_anchor.variant === 'plugin');
      const targetNode = commands.find((cmd) => cmd.node.id === pluginTarget?.edge.target);

      if (targetNode?.connections) {
        const connections = targetNode.connections.filter(
          (connection) =>
            connection?.edge?.target === targetNode?.node?.id && connection?.target_anchor?.variant === 'data'
        );

        if (command.config.identifier === 'Tool' && allowToolArguments) return connections.concat(command.connections);
        return connections;
      }
    }

    const connections = command.connections.filter(
      (connection) => connection?.edge?.target === command?.node?.id && connection?.target_anchor?.variant === 'data'
    );

    return connections;
  };

  /**
   * A module node derrives its return data by combining the possible values from
   * all Response nodes inside the module workflow. This method will return all of the
   * connections attached to all of the Response node inside a module.
   * @param moduleCommand The module node to determine the Response connections for.
   */

  type ModuleResponseConnections = {
    targetCommand: Command;
    connections: FullEdge[];
  };

  const getModuleResponseConnections = (moduleCommand: Command): ModuleResponseConnections[] => {
    if (!moduleCommand || moduleCommand.config.command_id !== 'node.module') return [];

    const moduleState = moduleCommand.state as ModNodeState;
    const moduleId = moduleState.moduleId;
    const moduleWorkflow = modules.find((mod) => mod.flow_id === moduleId);

    if (!moduleWorkflow) return [];

    const moduleWorkflowCommands = moduleWorkflow.commands;
    const moduleResponseNodes = moduleWorkflowCommands.filter(
      (command) => command.config.command_id === 'http.response'
    );

    const responseCommands: ModuleResponseConnections[] = [];

    for (const command of moduleResponseNodes) {
      const targetAnchor = command.config.anchors.find((anchor) => anchor.id === 'http.response.data');
      const sourceEdges = command.connections.filter((connection) => connection.target_anchor.id === targetAnchor?.id);

      for (const sourceEdge of sourceEdges) {
        const sourceCommand = moduleWorkflowCommands.find(
          (modCommand) => modCommand.node.id === sourceEdge?.edge?.source
        );

        if (targetAnchor && sourceCommand) {
          if (responseCommands.every((responseCommand) => responseCommand.targetCommand.node.id !== command.node.id)) {
            const connections = getInputDataConnections(command.node.id, moduleWorkflowCommands);
            if (connections !== undefined && connections !== null)
              responseCommands.push({ targetCommand: command, connections });
          }
        }
      }
    }

    return responseCommands;
  };

  /**
   * Merge the data for two JSON objects.
   * Currently used to merge Response outputs for all Response nodes in a module workflow.
   * @param obj1 The first object to merge.
   * @param obj2 The second object to merge.
   * @returns Both obj1 and obj2 merged together into a single JSON object.
   */

  const mergeJsonObjects = (obj1: Record<string, any>, obj2: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = { ...obj1 };

    for (const key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        if (key in result && typeof result[key] === 'object' && typeof obj2[key] === 'object') {
          result[key] = mergeJsonObjects(result[key], obj2[key]);
        } else {
          result[key] = obj2[key];
        }
      }
    }

    return result;
  };

  const moduleResponseConnectionsToData = (
    moduleWorkflow: Tab,
    moduleResponses: ModuleResponseConnections[],
    fold = false
  ) => {
    const moduleCommands = moduleWorkflow?.commands || [];

    let outputData = {};

    const getNodeOutputData = (targetCommand: Command, targetConnections: FullEdge[]) => {
      const dataCommands = getCommands(targetConnections, moduleCommands);
      if (dataCommands === undefined || dataCommands === null) return 'null';

      const isCustomSchema = targetCommand?.state?.format_response === 'true';
      if (isCustomSchema) {
        const dynamicOutput = targetCommand.output.dynamic;
        if (dynamicOutput) return dynamicOutput;

        try {
          return JSON.parse(targetCommand.state.format_schema.value);
        } catch {
          return undefined;
        }
      }

      try {
        const output = parseInputData(dataCommands, fold, moduleCommands);
        return output;
      } catch {
        return undefined;
      }
    };

    for (const response of moduleResponses) {
      try {
        const output = getNodeOutputData(response.targetCommand, response.connections);
        if (output && output !== 'null') outputData = mergeJsonObjects(outputData, output);
      } catch (e) {
        console.error('Failed to load input data', e);
      }
    }

    try {
      if (Object.keys(outputData).length === 0) return 'null';
      return outputData;
    } catch {
      return outputData;
    }
  };

  const getNodeOutput = (command: Command, anchor: AnchorType): object | null => {
    const command_id = command?.config?.command_id;

    if (command?.config?.command_id === 'node.module') {
      const moduleId = command?.state?.moduleId;
      const moduleResponseConnections = getModuleResponseConnections(command);
      const moduleWorkflow = modules.find((mod) => mod.flow_id === moduleId);

      if (moduleWorkflow) {
        const outputData = moduleResponseConnectionsToData(moduleWorkflow, moduleResponseConnections);
        return outputData;
      }
    }

    if (command?.config?.command_id === 'workflow.module') {
      const output = getModuleTriggerOutputData(command.node.id);
      if (output?.workflow) return output?.workflow;
      return null;
    }

    if (!command?.output?.default) return null;

    // if JSON mode is enabled, the completion node outputs different data
    if (command_id === 'llm.completion') {
      const isJsonModeEnabled = command.state.json_mode.enabled;
      const jsonFields = command.state.json_mode.args;

      if (isJsonModeEnabled && jsonFields.length > 0) {
        const output: { [key: string]: string } = {};

        for (const field of jsonFields) {
          const value = command?.output?.dynamic?.llm?.completion?.args?.[field.name];
          output[field.name] = value || '';
        }

        return { llm: { completion: { args: output } } };
      }
    }

    /* webhook nodes can have multiple data anchors, we use the anchorId 
      to determine the correct data object */

    const executionAnchor = getWebhookExecutionAnchor(command, anchor);
    if (executionAnchor) {
      const webhookId = executionAnchor.id;
      const webhookPayload = command?.output?.default?.[webhookId];
      return webhookPayload;
    }

    if (command?.output?.dynamic) return command.output.dynamic;

    const defaultValue = command?.output?.default;
    return defaultValue;
  };

  /**
   * Webhooks are a different type of execution anchor because
   * each webhook has it's own associated data anchor.
   * This method takes a data anchor and gets the associated execution
   * anchor if it exists. Returns null for non-webhook data anchors.
   */

  const getWebhookExecutionAnchor = (command: Command, anchor: AnchorType) => {
    try {
      // we do not need to consider this implementation if the node only has a single data anchor
      const anchors = command.config.anchors || [];
      const webhooks = command.config?.webhooks || [];

      const id = anchor.id;
      const attributes = id.split('.');

      /* data anchor id's are structured like this: call.answered.output_data
         the webhookId is retrieved by popping the last item and rejoining */

      const webhook_id = attributes.slice(0, -1).join('.');

      const webhookExists = webhooks.find((item) => item.webhook === webhook_id);
      if (!webhookExists) return null;

      const webhookExecutionAnchor = anchors.find((webhookAnchor) => webhookAnchor.id === webhook_id);
      return webhookExecutionAnchor;
    } catch (e) {
      return null;
    }
  };

  /**
   * Used to get the associated data anchor for a webhook execution anchor
   * @param command The command of the node
   * @param anchor The anchor of the node
   * @returns The associated execution anchor, or null if it does not exist
   */

  const getWebhookDataAnchor = (command: Command | undefined, sourceHandle: string | null | undefined) => {
    try {
      if (!command || !sourceHandle) return null;

      // we do not need to consider this implementation if the node only has a single data anchor
      const anchors = command.config.anchors || [];

      const executionAnchors = anchors.filter((anc) => anc.variant === 'webhook_execution' && anc.type === 'source');
      if (executionAnchors.length === 1) return null;

      const webhook_id = `${sourceHandle}.output_data`;
      const webhookDataAnchor = anchors.find((webhookAnchor) => webhookAnchor.id === webhook_id);

      return webhookDataAnchor;
    } catch (e) {
      return null;
    }
  };

  const truncateObject = (
    obj: object | string | number | boolean,
    depth: number
  ): object | string | number | boolean => {
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return obj;
    }

    if (depth === 0 || typeof obj !== 'object' || obj === null) {
      return '{...}';
    }

    // Iterate through the keys of the object and apply truncation recursively
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key as keyof typeof obj];
      acc[key] = truncateObject(value, depth - 1);
      return acc;
    }, {} as Record<string, any>);
  };

  const hasDuplicateNodeType = (
    commands: Command[],
    sourceAnchors: SourceAnchorType[],
    isModuleCheck = false
  ): boolean => {
    const typeNodeMap = new Map<string, Set<string>>();
    const anchorIdNodeMap = new Map<string, boolean>();

    if (hasDuplicateModuleConnection(sourceAnchors)) return true;

    for (const anchor of sourceAnchors) {
      if (anchor?.id) {
        if (anchorIdNodeMap.has(anchor.id)) return true;
        anchorIdNodeMap.set(anchor.id, true);
      }
    }

    for (const command of commands) {
      if (command?.state?.moduleId && !isModuleCheck) {
        const moduleCommand = modules.find((mod) => mod.flow_id === command?.state?.moduleId);
        const moduleResponseNodes =
          moduleCommand?.commands.filter((command) => command.config.command_id === 'http.response') || [];

        const responseInputCommands: Command[] = [];

        for (const responseNode of moduleResponseNodes) {
          const inputConnections = responseNode.connections.filter((connection) =>
            connection.source_anchor.variant.includes('execution')
          );

          const inputCommands = inputConnections.map((connection) =>
            moduleCommand?.commands?.find((command) => command.node.id === connection.edge.source)
          ) as Command[];

          responseInputCommands.push(...inputCommands);
        }

        const duplicates = hasDuplicateNodeType(responseInputCommands.concat(commands), sourceAnchors, true);
        if (duplicates) return true;
      }

      const type = command.node.type as string;
      const nodeId = command.node.id;

      // Get the set of node IDs for this type
      const nodeIds = typeNodeMap.get(type) || new Set<string>();

      // If we already have a different node with the same type, return true
      if (nodeIds.size > 0 && !nodeIds.has(nodeId)) {
        return true;
      }

      // Add this node ID to the set for this type
      nodeIds.add(nodeId);
      typeNodeMap.set(type, nodeIds);
    }

    // No duplicates found
    return false;
  };

  /**
   * Check all the return data edges of the nodes inside a module workflow.
   * If an edge exists in the parent workflow, and an edge exists in the module workflow,
   * it returns true. This means the same webhook anchor is being used to output data in both.
   * @param targetAnchors The anchors
   * @returns
   */

  const hasDuplicateModuleConnection = (sourceAnchors: SourceAnchorType[]) => {
    for (const anchor of sourceAnchors) {
      const command = commands.find((command) => command.node.id === anchor.source);
      const isModule = command?.config?.command_id === 'node.module';

      if (isModule) {
        const moduleCommandConnections = getModuleResponseConnections(command);
        const edgeExists = moduleCommandConnections.some((item) =>
          item.connections.some((conn) => sourceAnchors.some((source) => source.id === conn.source_anchor.id))
        );

        if (edgeExists) return true;
      }
    }

    return false;
  };

  /**
   * Gets the webhook ID for a data or execution anchor on a webhook node
   * @param anchor The anchor to get the webhookId for
   * @returns The webhookId or null if not applicable
   */

  const getWebhookId = (command: Command, anchor: AnchorType, allowData = true): string | null => {
    try {
      const hasWebhooks = command?.config?.webhooks && command?.config?.webhooks?.length > 0;
      if (!hasWebhooks) return null;

      if (anchor.variant === 'data' && allowData) {
        const id = anchor.id;
        const webhook_id = id.split('.').slice(0, -1).join('.');
        return webhook_id;
      }

      if (anchor.variant === 'webhook_execution') {
        return anchor.id;
      }

      return null;
    } catch (e) {
      console.error('Failed webhook', e);
      return null;
    }
  };

  return {
    commands,
    getInputData,
    getOutputData,
    getCommands,
    getInputDataConnections,
    getOptions,
    dataEdges,
    isStreamDisabled,
    getCommand,
    isSourceStreamEnabled,
    getStreamEdge,
    getDataConnections,
    getPluginDataConnections,
    getWebhookDataAnchor,
    getWebhookId,
    getNodeOutput,
    hasDuplicateNodeType,
    getModuleResponseConnections,
  };
};
